import * as signalr from "@microsoft/signalr";
import { Observable, ReplaySubject } from "rxjs";
import { HttpClient } from "@angular/common/http";
import {
    SwVersionEvent,
    AppVersionInfo,
    AppVersionEvent,
    AppVersionChange,
    AppVersionHubHandler,
    AppVersionUpdateAction
} from "@main/app-versioning/app-versioning.models";
import { AppStorage } from "@bbwt/utils/app-storage";
import { DestroyRef, Injectable } from "@angular/core";
import { SwUpdate, VersionEvent } from "@angular/service-worker";
import { AppVersionHub } from "@main/app-versioning/api/app-version.hub";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";


@Injectable()
export class AppVersionService extends BaseDataService {
    public readonly url: string = "api/versioning";

    public static readonly AppStorageItemKey: string = "app-version";
    public static readonly ResponseHeaderItemKey: string = "app-version";
    public static readonly DevelopEnvironmentVersion: string = "develop";

    private appVersionHubConnection: signalr.HubConnection;

    // Actions to perform before cleaning the cache and updating the app.
    private appVersionUpdateActions: AppVersionUpdateAction[] = [];

    // Subject that stores and emit the last version of the app to every new subscription.
    private versionChangesSubject: ReplaySubject<AppVersionChange> = new ReplaySubject(1);

    // Wrapper observable of the version changes subject, to avoid external modification.
    public versionChanges: Observable<AppVersionChange> = this.versionChangesSubject.asObservable();

    // Instances current version from storage at the beginning: if left null, the banner would trigger on any refresh.
    private current: string = AppStorage.getItem(AppVersionService.AppStorageItemKey);

    // Pending new version, waiting for update to substitute the current one.
    private pending: string = null;

    constructor(private swUpdate: SwUpdate,
                private appVersionHub: AppVersionHub,
                handlersFactory: HttpResponsesHandlersFactory,
                destroyRef: DestroyRef,
                http: HttpClient) {
        super(http, handlersFactory);

        // Listen for SignalR events notifying the current version of the app.
        this.registerVersionListener();

        // Clean the listener on service destruction.
        destroyRef.onDestroy(() => this.clearVersionListener());

        // Listen for SW events notifying the current version of the service workers.
        this.registerSwVersionListener();
    }

    public async getFreshVersion(): Promise<AppVersionInfo> {
        return await this.httpGet<AppVersionInfo>();
    }

    public async compareFreshVersion(): Promise<void> {
        const info: AppVersionInfo = await this.getFreshVersion();
        this.compareVersion(info.version);
    }

    public compareVersion(version: string): void {
        // If there is no version value, or the version didn't change, return.
        if (!version || version === this.current) return;

        //  If the environment is development, ignore the version and return.
        if (version === AppVersionService.DevelopEnvironmentVersion) return;

        // If this version is already pending, avoid notifying about it multiple times.
        if (version === this.pending) return;

        // At this point, if there was a registered version before, then it changed.
        this.versionChangesSubject.next({ current: this.current, pending: version });

        this.pending = version;
    }

    public async updateVersion(): Promise<void> {
        // Actions to perform before the version update.
        this.appVersionUpdateActions.forEach(action => action(this.pending));

        // Clear the whole cache, including service workers
        await this.clearCache();

        // Register the new version of the app.
        this.registerVersion(this.pending);

        // Update SW version if applies.
        await this.updateSwVersion();

        // Reload the page.
        window.location.reload();
    }

    public async updateSwVersion(): Promise<void> {
        try {
            // Activate SW version if a new one is ready.
            const success: boolean = await this.swUpdate.activateUpdate();

            if (!success) return;

            // If a new version was activated, redirect to pwa-cache.
            window.location.assign(window.location.href + "#pwacache");

        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn("Failed to update SW:", error);
        }
    }

    public cancelPendingUpdate(): void {
        this.pending = null;
    }

    private async clearCache(): Promise<void> {
        try {
            // Clear all cache storage.
            if ("caches" in window) {
                const cacheNames: string[] = await caches.keys();

                // Remove each cache item from the cache.
                await Promise.all(cacheNames.map(name => caches.delete(name)));
            }

            // Clear service workers.
            if ("serviceWorker" in navigator) {
                const registrations: readonly ServiceWorkerRegistration[] =
                    await navigator.serviceWorker.getRegistrations();

                // Unregister each service worker.
                await Promise.all(registrations.map(registration => registration.unregister()));
            }

        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn("Failed to clear cache:", error);
        }
    }

    private registerVersion(version: string): void {
        if (!version?.length) return;

        // Register the app version in the storage to be able to restore it later.
        AppStorage.setItem(AppVersionService.AppStorageItemKey, version);

        // Register the app version also in the service.
        this.current = version;
        this.pending = null;
    }

    private registerVersionListener(): void {
        const listener: string = AppVersionService.name;
        const handler: AppVersionHubHandler = this.compareVersion.bind(this);

        // Listen for SignalR events indicating the current version of the app.
        this.appVersionHubConnection = this.appVersionHub.getListeningConnection([
            { event: AppVersionEvent.Version, handler: handler, listener: listener },
        ]);
    }

    private registerSwVersionListener(): void {
        // If the service workers are enabled, subscribe to SW version events.
        if (!this.swUpdate.isEnabled) return;

        this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => this.checkSwVersionEvent(event));
    }

    public registerUpdateActions(actions: AppVersionUpdateAction[]): void {
        // Register actions to perform before the update of the application version.
        this.appVersionUpdateActions = [...this.appVersionUpdateActions, ...actions];
    }

    private async checkSwVersionEvent(event: VersionEvent): Promise<void> {
        // If the event is the version-ready event, trigger app version check.
        // This method can be extended to handle other SW events.
        switch (event.type) {
            case SwVersionEvent.Ready:
                await this.compareFreshVersion();
                break;
            case SwVersionEvent.Detected:
                // eslint-disable-next-line no-console
                console.info("New SW version detected.")
                break;
            case SwVersionEvent.NoDetected:
                // eslint-disable-next-line no-console
                console.info("No new SW version detected.")
                break;
            case SwVersionEvent.InstallationFailed:
                // eslint-disable-next-line no-console
                console.warn("New SW version installation failed.")
                break;
        }
    }

    private clearVersionListener(): void {
        if (!this.appVersionHubConnection) return;

        this.appVersionHub.releaseConnection(this.appVersionHubConnection);
    }
}